import { useMarketIntelStore } from '@/pages/market-intel/market-intel.store';
import { chartColor, chartColorPinned } from '@/shared/lib/chart-theme';
import { getAlertTopCategoriesQueryOptions } from '@/widgets/market-intel-tabs/api/get-alert-top-categories.query';
import { useQuery } from '@tanstack/react-query';
import type { Chart, ChartOptions } from 'chart.js';
import type { Dayjs } from 'dayjs';
import { useRef, type FC } from 'react';
import { Loader } from '../loader';
import { TooltipElement } from '../tooltip';
import { Typography } from '../typography';
import {
  basicStackedHorizontalBarBackgroundPlugin,
  HorizontalBarChart,
} from './horizontal-bar-chart';

const VISIBLE_BARS = 3;

const BAR_COLORS = [
  '#6D92CC',
  '#B37FEB',
  '#69C0FF',
  '#FF85C0',
  '#EB2F96',
  '#91D5FF',
  '#FFADD2',
  '#D3ADF7',
  '#FFD6E7',
  '#12408A',
  chartColor('--color-primary-5'),
  '#98B2DD',
  '#C3D3ED',
];
// A function, not a constant: `chartColor` reads the live CSS variables, so
// evaluating this at module scope would freeze it to whichever theme happened to
// be active at import time.
const otherBarColor = () => chartColorPinned('--color-neutral-6', '#bdbdbd');

type TopAlertsCategoriesChartProps = {
  facilityId: number;
  comparisonSet: 'INTERNAL' | 'COMP';
  from: Dayjs;
  to: Dayjs;
  hideTooltip?: boolean;
  showLabels?: boolean;
};

export const TopAlertsCategoriesChart: FC<TopAlertsCategoriesChartProps> = ({
  hideTooltip = false,
  showLabels = true,
  facilityId,
  comparisonSet,
  from,
  to,
}) => {
  const chart1Ref = useRef<Chart<'bar'> | null>(null);
  const chart2Ref = useRef<Chart<'bar'> | null>(null);

  const colorMap = new Map<string, string>();

  const isCompSet = useMarketIntelStore((state) => state.isCompSet);

  const { data: categories, isLoading } = useQuery(
    getAlertTopCategoriesQueryOptions({
      facilityId,
      comparisonSet,
      from: from.format('YYYY-MM'),
      to: to.format('YYYY-MM'),
    })
  );

  if (isLoading) {
    return (
      <div className="bg-neutral-1 border-neutral-4 w-full items-center justify-center border">
        <Loader />
      </div>
    );
  }

  const comparisonSetLabels =
    categories?.market.map((category) => category.alertName) ?? [];
  const yourFacilityLabels =
    categories?.facility.map((category) => category.alertName) ?? [];

  const comparisonSetDataset =
    categories?.market.map((category) => category.alertPct) ?? [];
  const yourFacilityDataset =
    categories?.facility.map((category) => category.alertPct) ?? [];

  const getBarColor = (label: string) => {
    if (label === 'Other') return otherBarColor();

    if (!colorMap.has(label)) {
      colorMap.set(label, BAR_COLORS[colorMap.size]);
    }

    return colorMap.get(label)!;
  };

  const setOtherBarColors = (
    chart: Chart<'bar'>,
    hoveredDatasetLabel: string | null | '*'
  ) => {
    chart.data.datasets.forEach((dataset, i) => {
      if (i === 0) return;

      const isMatch =
        hoveredDatasetLabel === '*' || dataset.label === hoveredDatasetLabel;

      const color = isMatch
        ? getBarColor(dataset.label ?? '')
        : otherBarColor();

      (dataset.backgroundColor as string[]) = [color];
    });

    chart.update('none');
  };

  const highlightMatchingBar = (
    hoveredLabel: string | null,
    hoveredDatasetLabel: string | null,
    sourceChart: Chart<'bar'>
  ) => {
    [chart1Ref, chart2Ref].forEach(({ current: chart }) => {
      if (!chart) return;

      const isSource = chart === sourceChart;

      if (hoveredLabel === null) {
        chart.setActiveElements([]);
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
        setOtherBarColors(chart, null);
        chart.update('none');
        return;
      }

      const labels = chart.data.labels as string[];
      const active: { datasetIndex: number; index: number }[] = [];

      const findDataset = (label: string) =>
        chart.data.datasets.findIndex((ds) => ds.label === label);

      const otherIndex = labels.indexOf('Other');
      const top3Index = (label: string) => labels.indexOf(label);

      if (hoveredLabel === 'Other') {
        setOtherBarColors(chart, isSource ? '*' : hoveredDatasetLabel);

        const dsIndex = findDataset(hoveredDatasetLabel ?? '');

        if (otherIndex !== -1 && dsIndex !== -1) {
          active.push({ datasetIndex: dsIndex, index: otherIndex });
        }

        const t3 = top3Index(hoveredDatasetLabel ?? '');

        if (t3 !== -1) {
          active.push({ datasetIndex: 0, index: t3 });
        }
      } else {
        setOtherBarColors(chart, isSource ? null : hoveredLabel);

        const t3 = top3Index(hoveredLabel);

        if (t3 !== -1) {
          active.push({ datasetIndex: 0, index: t3 });
        }

        if (!isSource) {
          const dsIndex = findDataset(hoveredLabel);

          if (otherIndex !== -1 && dsIndex !== -1) {
            active.push({ datasetIndex: dsIndex, index: otherIndex });
          }
        }
      }

      chart.setActiveElements(active);
      chart.tooltip?.setActiveElements(active, { x: 0, y: 0 });
      chart.update('none');
    });
  };

  const buildChartData = (labels: string[], data: number[], name: string) => {
    const topLabels = labels.slice(0, VISIBLE_BARS);
    const topData = data.slice(0, VISIBLE_BARS);
    const otherLabels = labels.slice(VISIBLE_BARS);
    const otherData = data.slice(VISIBLE_BARS);

    const hasOther = otherData.length > 0;
    const chartLabels = hasOther ? [...topLabels, 'Other'] : topLabels;

    const baseDataset = {
      name,
      data: [...topData, ...(hasOther ? [null] : [])],
      backgroundColor: topLabels.map((l) => getBarColor(l)),
      barThickness: 22,
      stack: 'stack',
    };

    const stackedDatasets = hasOther
      ? otherData.map((v, i) => ({
          label: otherLabels[i],
          data: [null, null, null, i === 0 ? v : v],
          backgroundColor: otherBarColor(),
          barThickness: 22,
          stack: 'stack',
        }))
      : [];

    return {
      labels: chartLabels,
      datasets: [baseDataset, ...stackedDatasets],
    };
  };

  const comparisonSetChartData = buildChartData(
    comparisonSetLabels,
    comparisonSetDataset,
    'Comparison Set'
  );

  const yourFacilityChartData = buildChartData(
    yourFacilityLabels,
    yourFacilityDataset,
    'Your Facility'
  );

  const options: ChartOptions<'bar'> = {
    interaction: {
      mode: 'nearest' as const,
      intersect: true,
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 10,
        },
        grid: {
          color: chartColorPinned('--color-neutral-5', '#cccccc'),
          drawTicks: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          color: chartColorPinned('--color-neutral-5', '#cccccc'),
          drawTicks: false,
        },
        border: { display: true, color: chartColor('--color-neutral-7') },
        ticks: {
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            const MAX = 12;
            return label.length > MAX ? label.slice(0, MAX) + '…' : label;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        yAlign: 'bottom',
        xAlign: 'center',
        backgroundColor: chartColor('--color-neutral-1'),
        bodyColor: chartColorPinned('--color-primary-black', '#000000d8'),
        displayColors: false,
        padding: 12,
        borderWidth: 1,
        borderColor: chartColor('--color-neutral-5'),
        bodyFont: { size: 14 },
        filter: (tooltipItem) => {
          const value = tooltipItem.raw;
          return value !== null && value !== undefined;
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            const isTop3Bar = context.dataIndex < VISIBLE_BARS;
            const value = Number(context.raw).toFixed(2);
            const label = isTop3Bar ? context.label : context.dataset.label;
            return `${label}: ${value}%`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: chartColorPinned('--color-neutral-8', '#666666'),
        display: (ctx) => {
          if (!showLabels) return false;
          const value = ctx.dataset.data[ctx.dataIndex];

          if (ctx.dataIndex === 3) {
            const datasets = ctx.chart.data.datasets;
            const lastDatasetIndex = datasets.reduce((last, ds, i) => {
              return i > 0 && ds.data[3] !== null ? i : last;
            }, -1);

            return ctx.datasetIndex === lastDatasetIndex;
          }

          return typeof value === 'number' && value !== null;
        },
        formatter: (value, ctx) => {
          if (ctx.dataIndex === VISIBLE_BARS) {
            return ctx.chart.data.datasets
              .slice(1)
              .reduce((sum, ds) => {
                const v = ds.data[VISIBLE_BARS];
                return typeof v === 'number' ? sum + v : sum;
              }, 0)
              .toFixed(2);
          }

          return typeof value === 'number' ? value.toFixed(2) : '';
        },
      },
    },
    onHover: (_, elements, chart) => {
      const hoveredLabel = elements[0]
        ? (chart.data.labels as string[])[elements[0].index]
        : null;
      const hoveredDatasetLabel = elements[0]
        ? (chart.data.datasets[elements[0].datasetIndex].label ?? null)
        : null;

      highlightMatchingBar(
        hoveredLabel,
        hoveredDatasetLabel,
        chart as Chart<'bar'>
      );
    },
  };

  return (
    <div className="bg-neutral-1 border-neutral-4 w-full border p-3 pb-10">
      <div className="flex items-center gap-x-2">
        <Typography as="h5" variant="h5" className="font-medium">
          Top Alerts Categories, %
        </Typography>
        {!hideTooltip && (
          <TooltipElement content="Shows the percentage distribution of all created alerts by category during the selected period" />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-y-12">
        <div className="h-[150px] w-full">
          <Typography as="h5" variant="h5" className="text-sm font-medium">
            {isCompSet ? 'Market' : 'Portfolio'}
          </Typography>
          <HorizontalBarChart
            ref={chart1Ref}
            options={options}
            data={comparisonSetChartData}
            plugins={[basicStackedHorizontalBarBackgroundPlugin]}
          />
        </div>

        <div className="h-[150px] w-full">
          <Typography as="h5" variant="h5" className="text-sm font-medium">
            Your Facility
          </Typography>
          {categories && categories.facility.length > 0 ? (
            <HorizontalBarChart
              ref={chart2Ref}
              options={options}
              data={yourFacilityChartData}
              plugins={[basicStackedHorizontalBarBackgroundPlugin]}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Typography className="text-neutral-8">
                No alerts generated for this period
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
